import * as R from 'ramda'
import { of } from 'rxjs'
import { catchError, delayWhen, distinctUntilChanged, filter, map, switchMap, startWith } from 'rxjs/operators'
import { ofType } from 'redux-observable'
import { LOCATION_CHANGE } from 'connected-react-router'
import { queryFromString } from 'helpers/url'
import { captureException } from 'helpers/raven'
import { DATE_FILTERS_FORMAT } from 'constants/dateFormats'
import { isProductPageAction } from 'domain/router'
import { isProductFetched } from 'domain/resources'
import * as Actions from 'store/actions/index.ts'
import {
  getBeginDate,
  getCountryCode,
  getCultureCode,
  getCurrentProductKey,
  getDepartureCitiesFiltersFromQuery,
  getDurationsFiltersFromQuery,
  getFiltersProducts,
  getIsSaleFetched,
  getMarketId,
  getOperationCode,
  getSaleKey,
  getSelectedDepartureDatesOffsetsRangeListFromQuery,
  getShouldDoubleThePriceFeatureEnabled
} from 'store/selectors'
import { getDepartureDatesFromOffset } from 'domain/filters'
import { awaitForProductFetched } from 'store/epics/helpers'
import { fetchFilteredPackages } from 'api/planet/filters'
import { isSamePage } from '../helpers/router'

const fetchFilteredPackagesEpic = (actions$, state$) => actions$.pipe(
  ofType(LOCATION_CHANGE),
  distinctUntilChanged(R.eqBy(R.path(['payload', 'location', 'pathname']))),
  filter(isProductPageAction),
  delayWhen(locationChangeAction => {
    const resourceFetched$ = actions$.pipe(ofType(Actions.resources.RESOURCE_FETCHED))
    const locationChangeKey = queryFromString(locationChangeAction.payload.location.search).key
    return (getIsSaleFetched(state$.value) && isProductFetched(locationChangeKey)(state$.value))
      ? of({})
      : awaitForProductFetched(locationChangeKey, resourceFetched$, state$)
  }),
  delayWhen(() => {
    const filtersProducts = getFiltersProducts(state$.value)

    return Object.keys(filtersProducts).length > 0
      ? of({})
      : actions$.pipe(ofType(Actions.filters.FILTERS_FETCHED))
  }),
  filter(locationChangeAction => isSamePage(locationChangeAction, state$.value)),
  switchMap(() => {
    const state = state$.value

    const beginDate = getBeginDate(state)
    const countryCode = getCountryCode(state)
    const cultureCode = getCultureCode(state)
    const departureCities = getDepartureCitiesFiltersFromQuery(state)
    const departureDatesRanges = getSelectedDepartureDatesOffsetsRangeListFromQuery(state)
    const durations = getDurationsFiltersFromQuery(state)
    const formattedDepartureDateOffset = getDepartureDatesFromOffset(beginDate, DATE_FILTERS_FORMAT)(departureDatesRanges)
    const marketId = getMarketId(state)
    const operationCode = getOperationCode(state)
    const productKey = getCurrentProductKey(state)
    const saleKey = getSaleKey(state)
    const isResourceV2 = getShouldDoubleThePriceFeatureEnabled(state)

    return fetchFilteredPackages(
      countryCode,
      cultureCode,
      departureCities,
      formattedDepartureDateOffset,
      durations,
      marketId,
      operationCode,
      productKey,
      saleKey,
      isResourceV2
    ).pipe(
      map(({ response }) => Actions.filters.onFetchFilteredPackagesSuccess(productKey, response.packages)),
      startWith(Actions.filters.onFetchFilteredPackagesStart()),
      catchError((error) => {
        captureException(new Error('Unable to fetch filtered packages: /search/Packages'), {
          extra: { error }
        })

        return of(Actions.filters.onFetchFilteredPackagesError())
      })
    )
  })
)

export default fetchFilteredPackagesEpic
